import type { TFunction } from 'i18next';

// WP1 part 2: an invoiced order stays editable, but its invoiced lines keep
// their reference (no removal, no unit change). Stable code → Polish copy.
const INVOICED_LINE_ERROR_KEYS: Record<string, string> = {
  ORDER_PRODUCT_INVOICED_LINE_REMOVAL_BLOCKED: 'orders:error.invoicedLineRemovalBlocked',
  ORDER_PRODUCT_INVOICED_UNIT_CHANGE_BLOCKED: 'orders:error.invoicedUnitChangeBlocked',
};

export const getOrderProductSaveErrorMessage = (t: TFunction, error: unknown, fallback: string) => {
  // API `message` / `error` values are diagnostic prose (often English), not
  // a stable Polish UI contract. Known actionable codes map to their own
  // copy; all other save failures use the caller's precise Polish fallback.
  const code = (error as { response?: { data?: { code?: unknown } } })?.response?.data?.code;
  if (typeof code === 'string' && INVOICED_LINE_ERROR_KEYS[code]) {
    return String(t(INVOICED_LINE_ERROR_KEYS[code]));
  }
  return fallback;
};

export interface OrderProductDeleteCargoConflictResponse {
  code?: unknown;
  routeNumbers?: unknown;
  productNames?: unknown;
}

const toStringList = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];

// S32 (order-product delete guard): PUT /orders/:id/products answers 400
// ORDER_PRODUCT_HAS_TRANSPORT_CARGO when the payload would delete a line that
// is still loaded on a transport (see orderProductsService
// .assertDeletableWithoutOrphaningCargo). The backend's `message` is an
// English sentence meant for logs/API consumers — map the stable `code` to
// Polish-first copy here instead of echoing it, same pattern as
// transportRouteViews.mapTransportMutationErrorToMessage. Returns undefined
// for any other error so callers fall through to
// getOrderProductSaveErrorMessage's generic message/fallback handling.
export const getOrderProductDeleteCargoConflictMessage = (
  t: TFunction,
  error: unknown,
): string | undefined => {
  const responseData = (error as { response?: { data?: OrderProductDeleteCargoConflictResponse } })
    ?.response?.data;

  if (responseData?.code !== 'ORDER_PRODUCT_HAS_TRANSPORT_CARGO') {
    return undefined;
  }

  return t('orders:orderDetail.messages.productHasTransportCargoError', {
    products: toStringList(responseData.productNames).join(', '),
    routes: toStringList(responseData.routeNumbers).join(', '),
  });
};
